/**
 * Admin API Service
 * Provides functions to interact with admin backend API endpoints
 */

import axios from 'axios';
import { getApiBaseUrl } from '../config';
import { getAccessToken } from '../utils/tokenStorage';

/**
 * Get the authorization header with access token
 */
function getAuthHeaders(): Record<string, string> {
  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }
  return {
    Authorization: `Bearer ${token}`,
  };
}

/**
 * Record last login timestamp for a user
 * @param userId - Cognito username
 * @param timestamp - ISO 8601 timestamp
 */
export async function recordLastLogin(userId: string, timestamp: string): Promise<void> {
  const apiUrl = getApiBaseUrl();
  await axios.post(
    `${apiUrl}/admin/users/${encodeURIComponent(userId)}/last-login`,
    { timestamp },
    { headers: getAuthHeaders() }
  );
}

/**
 * Retrieve last login timestamps for multiple users
 * @param userIds - Array of Cognito usernames
 * @returns Map of username to last login timestamp (null if never logged in)
 */
export async function getLastLoginBatch(userIds: string[]): Promise<Map<string, string | null>> {
  const apiUrl = getApiBaseUrl();
  const response = await axios.get(
    `${apiUrl}/admin/users/last-logins`,
    {
      params: { userIds: userIds.join(',') },
      headers: getAuthHeaders(),
    }
  );

  const lastLoginData = response.data.lastLogins as Record<string, string | null>;
  return new Map(Object.entries(lastLoginData));
}
